import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import SmartImage from './SmartImage';

/** Aligns with badge logic: images are not shown after this window from capture / last update. */
const DOCUMENT_IMAGE_RETENTION_MS = 7 * 24 * 60 * 60 * 1000;

function getDocumentImageRetentionState(capturedAt, lastUpdatedTime) {
  const base = capturedAt || lastUpdatedTime;
  if (!base) return { expired: false, exp: null };
  try {
    const cap = new Date(base);
    if (Number.isNaN(cap.getTime())) return { expired: false, exp: null };
    const exp = new Date(cap.getTime() + DOCUMENT_IMAGE_RETENTION_MS);
    return { expired: new Date() > exp, exp };
  } catch {
    return { expired: false, exp: null };
  }
}

// Helper function to get confidence styling
const getConfidenceStyle = (detail) => {
  const confidence = typeof detail.Confidence === 'string' ? parseFloat(detail.Confidence) : (detail.Confidence || 0);
  // Display rule: use fixed threshold of 25% for red/green, regardless of provided Threshold
  const threshold = 0.25;
  const isAboveThreshold = confidence >= threshold;
  
  return {
    variant: isAboveThreshold ? "outline" : "destructive",
    className: isAboveThreshold
      ? "bg-green-100 text-green-800 border-green-200"
      : "bg-red-100 text-red-800 border-red-200",
    percentage: Math.round(confidence * 100)
  };
};

// Helper function to get page by type
const getPageByType = (pages, pageType) => {
  return pages.find(page => 
    page.Page?.toLowerCase().includes(pageType.toLowerCase())
  ) || null;
};

// Helper function to get page display name
const getPageDisplayName = (pageType) => {
  switch (pageType.toLowerCase()) {
    case 'rgbpage1': return 'Page 1 RGB';
    case 'rgbpage2': return 'Page 2 RGB';
    case 'bwpage1': return 'Page 1 B&W';
    case 'bwpage2': return 'Page 2 B&W';
    default: return pageType;
  }
};

const DocumentDetailPanel = ({
  title,
  ocrDetails,
  documentDetails,
  lastUpdatedTime,
  capturedAt,
  imageAvailable = true,
  editable = false,
  onSave = () => {},
  onSubmitDvs = () => {},
  saving = false,
  submitting = false
}) => {
  // Get pages in the specified order: RGB 1 & 2, then BW 1 & 2
  const pageOrder = ['RGBPage1', 'RGBPage2', 'BWPage1', 'BWPage2'];
  
  const getPageForPosition = (pageType) => getPageByType(ocrDetails, pageType);

  // State and ref for scroll detection and zoom tracking
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const scrollContainerRef = useRef(null);
  const containerRef = useRef(null);

  // Handle zoom detection
  useEffect(() => {
    const updateZoomLevel = () => {
      const newZoom = window.devicePixelRatio || 1;
      setZoomLevel(newZoom);
    };

    // Initial zoom detection
    updateZoomLevel();

    // Listen for zoom changes
    window.addEventListener('resize', updateZoomLevel);
    const mediaQuery = window.matchMedia('screen');
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', updateZoomLevel);
    }

    return () => {
      window.removeEventListener('resize', updateZoomLevel);
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', updateZoomLevel);
      }
    };
  }, []);

  // Handle scroll events with improved logic
  useEffect(() => {
    const handleScroll = () => {
      if (scrollContainerRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
        const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10; // 10px tolerance
        const hasScrollableContent = scrollHeight > clientHeight;
        setShowScrollIndicator(hasScrollableContent && !isAtBottom);
      }
    };

    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
      // Initial check
      handleScroll();
      
      // Also check on resize
      const resizeObserver = new ResizeObserver(handleScroll);
      resizeObserver.observe(scrollContainer);
      
      return () => {
        scrollContainer.removeEventListener('scroll', handleScroll);
        resizeObserver.disconnect();
      };
    }
  }, [documentDetails]);
  // Local editable state
  const [detailsState, setDetailsState] = useState(() => documentDetails.map(d => ({ ...d })));
  const [editMode, setEditMode] = useState(false);
  const [hasSaved, setHasSaved] = useState(false);
  useEffect(() => {
    setDetailsState(documentDetails.map(d => ({ ...d })));
  }, [documentDetails]);

  const handleValueChange = (index, field, value) => {
    setDetailsState(prev => prev.map((item, i) => i === index ? { ...item, [field]: value } : item));
  };

  const handleSave = () => { setHasSaved(true); setEditMode(false); onSave(detailsState); };
  const handleSubmit = () => onSubmitDvs(detailsState);

  const { expired: documentImageRetentionExpired, exp: retentionExp } = getDocumentImageRetentionState(
    capturedAt,
    lastUpdatedTime
  );

  const renderAgingBadge = () => {
    try {
      const base = capturedAt || lastUpdatedTime;
      if (!base || !retentionExp) return null;
      const now = new Date();
      if (now > retentionExp) {
        return <Badge className="bg-red-100 text-red-800 border-red-200">Image expired</Badge>;
      }
      const daysLeft = Math.ceil((retentionExp.getTime() - now.getTime()) / (24 * 60 * 60 * 1000));
      const cls = daysLeft > 3
        ? 'bg-green-100 text-green-800 border-green-200'
        : daysLeft >= 1
          ? 'bg-yellow-100 text-yellow-800 border-yellow-200'
          : 'bg-red-100 text-red-800 border-red-200';
      return <Badge className={cls}>{`${daysLeft} day${daysLeft === 1 ? '' : 's'} left`}</Badge>;
    } catch {
      return null;
    }
  };

  return (
    <Card className="mb-8">
      <CardHeader className="pb-3 border-b">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CardTitle className="text-lg">{title}</CardTitle>
            {renderAgingBadge()}
          {lastUpdatedTime && (
            <Badge variant="outline">{(() => {
              // Format date consistently
              try {
                const date = new Date(lastUpdatedTime);
                const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                               'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                const day = date.getDate().toString().padStart(2, '0');
                const month = months[date.getMonth()];
                const year = date.getFullYear();
                
                const hasTime = lastUpdatedTime.includes('T') || lastUpdatedTime.includes(':') || lastUpdatedTime.includes(' ');
                
                if (hasTime && (date.getHours() !== 0 || date.getMinutes() !== 0 || date.getSeconds() !== 0)) {
                  const hours = date.getHours().toString().padStart(2, '0');
                  const minutes = date.getMinutes().toString().padStart(2, '0');
                  const seconds = date.getSeconds().toString().padStart(2, '0');
                  return `${day} ${month} ${year} ${hours}:${minutes}:${seconds}`;
                } else {
                  return `${day} ${month} ${year}`;
                }
              } catch {
                return lastUpdatedTime;
              }
            })()}</Badge>
          )}
          </div>
          {editable && (
            <div>
              {!editMode ? (
                <button type="button" onClick={() => { setEditMode(true); setHasSaved(false); }} className="text-sm text-psg-teal hover:underline">Edit</button>
              ) : (
                <button type="button" onClick={() => { setEditMode(false); setHasSaved(false); setDetailsState(documentDetails.map(d => ({ ...d }))) }} className="text-sm text-red-600 hover:underline">Cancel</button>
              )}
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="p-6">
        <div className="flex flex-col lg:flex-row gap-6 h-full">
          {/* Left side - 2x2 Image Grid */}
          <div className={imageAvailable && !documentImageRetentionExpired ? "lg:w-1/2" : "lg:w-1/3"}>
            <div className="grid grid-cols-2 gap-4">
              {pageOrder.map((pageType, index) => {
                const page = getPageForPosition(pageType);
                const isBW = pageType.toLowerCase().includes('bw');
                const showImage = Boolean(page?.PageURL) && !documentImageRetentionExpired;
                return (
                  <div key={`${pageType}-${index}`} className="aspect-square">
                    <div className="w-full h-full bg-gray-100 border rounded-md overflow-hidden">
                      {showImage ? (
                        <div className="relative w-full h-full">
                          <SmartImage
                            photoURL={page.PageURL}
                            className="w-full h-full object-contain"
                            style={isBW ? { filter: 'grayscale(100%) saturate(0%)' } : undefined}
                            alt={`${getPageDisplayName(pageType)} Document`}
                            placeholderType="document"
                            clickable={true}
                            modalTitle={`${getPageDisplayName(pageType)} Document Image`}
                          />
                          <div className="absolute bottom-2 left-2 bg-black bg-opacity-70 text-white px-2 py-1 rounded text-xs">
                            {getPageDisplayName(pageType)}
                          </div>
                        </div>
                      ) : page?.PageURL && documentImageRetentionExpired ? (
                        <div className="flex items-center justify-center h-full text-gray-500 flex-col p-2">
                          <p className="text-xs text-center font-medium mb-1">{getPageDisplayName(pageType)}</p>
                          <p className="text-xs text-center">Image not shown — retention period ended</p>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center h-full text-gray-400 flex-col">
                          <p className="text-xs text-center font-medium mb-1">{getPageDisplayName(pageType)}</p>
                          <p className="text-xs text-center">No Image Available</p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right side - Data Table with dynamic height */}
  <div className={imageAvailable && !documentImageRetentionExpired ? "lg:w-1/2 flex flex-col min-h-0" : "lg:w-2/3 flex flex-col min-h-0"}>
            <div className="border rounded-lg overflow-hidden flex flex-col flex-1">
              <div className="bg-gray-50 border-b flex-shrink-0">
                <div className="grid grid-cols-12 gap-4 p-3 font-medium text-sm text-gray-600">
                  <div className="col-span-4">Name</div>
                  <div className="col-span-5">Value</div>
                  <div className="col-span-3 text-center">OCR Confidence %</div>
                </div>
              </div>
      <div 
                ref={scrollContainerRef} 
                className="overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 flex-1"
                style={{ 
                  minHeight: '400px',
                  maxHeight: `calc(100vh - 400px)`,
                  height: `calc(${Math.max(500, window.innerHeight * 0.6)}px * ${zoomLevel})`
                }}
              >
        {detailsState && detailsState.length > 0 ? (
          detailsState.map((detail, index) => (
                    <div key={`detail-${index}`} className="grid grid-cols-12 gap-4 p-3 border-b last:border-b-0 hover:bg-gray-50">
                      <div className="col-span-4 text-sm font-medium text-gray-700">
                        {detail.Name}
                      </div>
              {editable && editMode ? (
                <>
                  <div className="col-span-5">
                    <input type="text" value={detail.Content || ''} onChange={e => handleValueChange(index, 'Content', e.target.value)} className="w-full px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-psg-teal" placeholder="Enter value" />
                  </div>
                  <div className="col-span-3">
                    <input type="number" min={0} max={100} step={1} value={Math.round((typeof detail.Confidence === 'string' ? parseFloat(detail.Confidence) : (detail.Confidence || 0)) * 100)} onChange={e => handleValueChange(index, 'Confidence', (parseInt(e.target.value || '0', 10) / 100))} className="w-full px-2 py-1 border border-gray-300 rounded-md text-sm text-center focus:outline-none focus:ring-2 focus:ring-psg-teal" />
                  </div>
                </>
              ) : (
                <>
                  <div className="col-span-5 text-sm text-gray-900">
                    {detail.Content || 'N/A'}
                  </div>
                  <div className="col-span-3 flex justify-center">
                    <Badge className={getConfidenceStyle(detail).className}>
                      {getConfidenceStyle(detail).percentage}%
                    </Badge>
                  </div>
                </>
              )}
                    </div>
                  ))
                ) : (
                  <div className="p-6 text-center text-gray-500">
                    No document details available
                  </div>
                )}
              </div>
            </div>
    {editable && editMode && (
      <div className="mt-3 flex items-center justify-end gap-2">
        <button type="button" onClick={handleSave} disabled={saving} className="btn-primary px-4 py-2 rounded-md disabled:opacity-50">{saving ? 'Saving...' : 'Save Changes'}</button>
      </div>
    )}

    {editable && !editMode && hasSaved && (
      <div className="mt-3 flex items-center justify-end gap-2">
        <button type="button" onClick={handleSubmit} disabled={submitting} className="border border-psg-teal text-psg-teal px-4 py-2 rounded-md hover:bg-psg-teal-50 disabled:opacity-50">{submitting ? 'Submitting...' : 'Submit for DVS'}</button>
      </div>
    )}
            
            {/* Compact scroll indicator - only show when needed */}
            {showScrollIndicator && (
              <div className="mt-2 text-center">
                <div className="inline-block bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs">
            ↓ Scroll for more ({detailsState.length} items)
                </div>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default DocumentDetailPanel;